import { useState, useEffect, useCallback } from "react";

import dayjs from "dayjs";

import { Invoice } from "../bitmaker/schemas/Billing/Invoice";

import { useApiError } from "./useApiError";

import { methodMap } from "../services/BitmakerApp";

export function useInvoice(invoiceId: string | undefined) {
  const { handleAPIError } = useApiError();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(!!invoiceId);
  const [invoiceFetchError, setInvoiceFetchError] = useState<string | null>(
    null,
  );

  const isExistingInvoice = !!invoiceId;

  const fetchInvoice = useCallback(() => {
    if (!invoiceId) return;

    setLoadingInvoice(true);
    setInvoiceFetchError(null);

    methodMap.get(`/billing/invoices/${invoiceId}`, null, (response) => {
      const { isError, message: apiMessage } = handleAPIError(response);

      if (isError) {
        setInvoiceFetchError(
          `Error loading invoice #${invoiceId}. Please try again.`,
        );
        console.error(`Error loading invoice #${invoiceId}:`, apiMessage);
        setLoadingInvoice(false);
        return;
      }

      const loadedInvoice = response.data as Invoice;

      if (loadedInvoice.issueDate) {
        loadedInvoice.issueDate = dayjs(loadedInvoice.issueDate) as any;
      }
      if (loadedInvoice.dueDate) {
        loadedInvoice.dueDate = dayjs(loadedInvoice.dueDate) as any;
      }
      if (loadedInvoice.termId == null && loadedInvoice.terms?.trim()) {
        loadedInvoice.termId = 0; // CUSTOM_TERM_VALUE
      }

      loadedInvoice.subtotal = parseFloat(loadedInvoice.subtotal as any) || 0;
      loadedInvoice.totalTax = parseFloat(loadedInvoice.totalTax as any) || 0;
      loadedInvoice.totalAmount =
        parseFloat(loadedInvoice.totalAmount as any) || 0;
      loadedInvoice.amountPaid =
        parseFloat(loadedInvoice.amountPaid as any) || 0;

      if (loadedInvoice.taxSummary && loadedInvoice.taxSummary.length) {
        loadedInvoice.taxSummary = loadedInvoice.taxSummary.map((summary) => ({
          ...summary,
          totalTaxAmount: parseFloat(summary.totalTaxAmount as any) || 0,
          taxRate: parseFloat(summary.taxRate as any) || 0,
        }));
      }

      if (loadedInvoice.items && loadedInvoice.items.length) {
        loadedInvoice.items = loadedInvoice.items.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity as any) || 0,
          unitPrice: parseFloat(item.unitPrice as any) || 0,
          lineSubtotal: parseFloat(item.lineSubtotal as any) || 0,
          lineTotalTax: parseFloat(item.lineTotalTax as any) || 0,
          lineTotal: parseFloat(item.lineTotal as any) || 0,
          appliedTaxes: item.appliedTaxes
            ? item.appliedTaxes.map((tax) => ({
                ...tax,
                taxRate: parseFloat(tax.taxRate as any) || 0,
                taxAmount: parseFloat(tax.taxAmount as any) || 0,
              }))
            : [],
        }));
      }

      setInvoice(loadedInvoice);
      setLoadingInvoice(false);
    });
  }, [invoiceId, handleAPIError]);

  useEffect(() => {
    if (isExistingInvoice) {
      fetchInvoice();
    }
  }, [isExistingInvoice, fetchInvoice]);

  return {
    invoice,
    loadingInvoice,
    invoiceFetchError,
    refetchInvoice: fetchInvoice,
  };
}
